import { Injectable, inject, signal, WritableSignal } from '@angular/core';
import { Capacitor, type PluginListenerHandle } from '@capacitor/core';
import { App } from '@capacitor/app';
import { OtaUpdate } from 'capacitor-ota-update';
import { environment } from '../../../environments/environment';
import { OTA_BUILD_EPOCH } from '../../../environments/ota-build-info';
import { NetworkService } from './network';

/**
 * Mises à jour Over-The-Air du bundle web.
 *
 * Interroge la Cloud Function `checkOtaUpdate` au démarrage puis à chaque retour
 * au premier plan (throttlé), télécharge + vérifie (côté natif : SHA-256 + ECDSA),
 * puis expose `updateAvailable` pour proposer un redémarrage — ou, pour une release
 * silencieuse, l'applique sans prompt (au prochain resume ou démarrage à froid).
 * No-op sur le web et quand `environment.ota.enabled` est false.
 *
 * Le téléchargement/vérification/application et le watchdog anti-crash (rollback
 * auto) vivent dans le plugin natif ; ce service ne fait qu'orchestrer et exposer
 * l'état UI.
 */
@Injectable({ providedIn: 'root' })
export class OtaUpdateService {
  private network = inject(NetworkService);

  /** Délai minimal entre deux vérifications déclenchées par un resume. */
  private static readonly RESUME_CHECK_THROTTLE_MS = 5 * 60 * 1000;

  /** Vrai dès qu'un bundle vérifié a été téléchargé et est prêt à appliquer. */
  readonly updateAvailable: WritableSignal<boolean> = signal(false);
  /** Version OTA active en cours d'exécution (null = build du store). */
  readonly currentVersion: WritableSignal<string | null> = signal(null);

  private checking = false;
  /** Vrai tant qu'un bundle vérifié est téléchargé mais pas encore appliqué (survit à un dismiss). */
  private pendingUpdateReady = false;
  /** Vrai quand le bundle en attente doit être appliqué silencieusement (sans modale). */
  private pendingIsSilent = false;
  /** Release OTA actuellement active sur l'appareil (anti-boucle : on ne se re-propose jamais soi-même). */
  private currentReleaseId: string | null = null;
  /** Release déjà téléchargée et en attente d'application (anti-re-téléchargement). */
  private pendingReleaseId: string | null = null;
  /** Epoch (ms) de la dernière vérification, pour throttler les checks au resume. */
  private lastCheckAt = 0;
  private resumeListenerBound = false;
  /** Handle du listener appStateChange, conservé pour pouvoir le retirer (cf. removeResumeListener). */
  private resumeHandle: PluginListenerHandle | null = null;

  /** Appelé une fois au démarrage de l'app (après le boot du web). */
  async initialize(): Promise<void> {
    if (!environment.ota.enabled || !Capacitor.isNativePlatform()) {
      return;
    }

    // Indique au watchdog anti-crash natif que le bundle web a démarré
    // correctement (réinitialise le compteur d'échecs : un bundle sain n'est
    // jamais auto-rollback).
    await OtaUpdate.notifyAppReady().catch(() => {});

    try {
      const info = await OtaUpdate.getCurrentVersion();
      this.currentVersion.set(info.version);
      this.currentReleaseId = info.releaseId;
    } catch {
      // non-bloquant
    }

    if (environment.ota.checkOnStartup) {
      void this.checkAndDownload();
    }

    // Pas de polling périodique (limite la consommation) : on vérifie au
    // démarrage et au resume (throttlé). Un bundle en attente est appliqué
    // (silencieux) ou re-proposé (modale) au resume.
    if (!this.resumeListenerBound) {
      try {
        this.resumeHandle = await App.addListener('appStateChange', ({ isActive }) => {
          if (isActive) {
            this.onResume();
          }
        });
        this.resumeListenerBound = true;
      } catch (e) {
        // Échec d'enregistrement : on garde `resumeListenerBound` à false pour autoriser
        // une nouvelle tentative, et on trace (sinon les checks au resume seraient muets).
        console.warn('[OTA] Échec de l’enregistrement du listener appStateChange', e);
      }
    }
  }

  /** Retire le listener appStateChange (le handle est conservé à l'enregistrement). */
  async removeResumeListener(): Promise<void> {
    await this.resumeHandle?.remove();
    this.resumeHandle = null;
    this.resumeListenerBound = false;
  }

  /** Gère un retour au premier plan. */
  private onResume(): void {
    // Un bundle vérifié est déjà téléchargé et attend d'être appliqué.
    if (this.pendingUpdateReady) {
      if (this.pendingIsSilent) {
        void this.applyAndReload();
      } else {
        this.updateAvailable.set(true);
      }
      return;
    }
    // Sinon on cherche une nouvelle MAJ, au plus une fois par fenêtre de throttle.
    if (Date.now() - this.lastCheckAt >= OtaUpdateService.RESUME_CHECK_THROTTLE_MS) {
      void this.checkAndDownload();
    }
  }

  /** Vérifie le channel et, si une MAJ est en rollout, la télécharge + vérifie. */
  async checkAndDownload(): Promise<void> {
    if (
      this.checking ||
      !environment.ota.enabled ||
      !Capacitor.isNativePlatform() ||
      !this.network.isOnline()
    ) {
      return;
    }
    this.checking = true;
    this.lastCheckAt = Date.now();
    try {
      const check = await OtaUpdate.checkForUpdate({
        appId: environment.ota.appId,
        channelId: environment.ota.channelId,
        apiBaseUrl: environment.ota.apiBaseUrl,
        currentBundleEpoch: OTA_BUILD_EPOCH,
      });

      if (
        !check.updateAvailable ||
        !check.bundleUrl ||
        !check.signature ||
        !check.hashSha256 ||
        !check.releaseId ||
        !check.version
      ) {
        return;
      }

      // Anti-boucle : la release proposée est déjà celle qui tourne → ne rien faire
      // (garde-fou si le serveur renvoie par erreur updateAvailable pour la release active).
      if (check.releaseId === this.currentReleaseId) {
        return;
      }

      // Déjà téléchargée et vérifiée, en attente d'application : ne pas re-télécharger,
      // simplement (re)proposer le redémarrage (sauf MAJ silencieuse).
      if (check.releaseId === this.pendingReleaseId && this.pendingUpdateReady) {
        // Le channel a pu basculer le flag `silent` entre-temps : on resynchronise.
        this.pendingIsSilent = check.silent === true;
        if (!this.pendingIsSilent) {
          this.updateAvailable.set(true);
        }
        return;
      }

      const result = await OtaUpdate.downloadUpdate({
        bundleUrl: check.bundleUrl,
        signature: check.signature,
        hashSha256: check.hashSha256,
        releaseId: check.releaseId,
        version: check.version,
      });

      if (result.success) {
        this.pendingReleaseId = check.releaseId;
        this.pendingUpdateReady = true;
        this.pendingIsSilent = check.silent === true;
        // Release silencieuse : pas de modale — appliquée au prochain resume ou
        // démarrage à froid.
        if (!this.pendingIsSilent) {
          this.updateAvailable.set(true);
        }
      }
    } catch {
      // Fail-open : ne jamais bloquer l'app sur une erreur OTA.
    } finally {
      this.checking = false;
    }
  }

  /** Applique le bundle téléchargé et recharge la WebView immédiatement. */
  async applyAndReload(): Promise<void> {
    if (!this.pendingUpdateReady) return;
    // La WebView va être rechargée. On promeut la release en attente au rang d'« active »
    // AVANT le reload : si Capacitor ne recrée pas le contexte JS (rechargement de page sans
    // nouveau process), `currentReleaseId` reste à jour et aucun re-check ne re-proposera la
    // même release (anti-boucle). Si le contexte est recréé, initialize() le redéfinira (idem).
    this.currentReleaseId = this.pendingReleaseId;
    this.pendingReleaseId = null;
    this.pendingUpdateReady = false;
    this.pendingIsSilent = false;
    this.updateAvailable.set(false);
    await OtaUpdate.applyUpdateAndReload();
  }

  /**
   * L'utilisateur reporte : on masque le prompt mais on garde la MAJ en attente.
   * Elle est appliquée automatiquement au prochain démarrage à froid, et le prompt
   * réapparaît à chaque retour au premier plan (cf. listener appStateChange dans
   * initialize) jusqu'à son application.
   */
  dismiss(): void {
    this.updateAvailable.set(false);
  }
}
